import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ToolCardComponent, ToolCardStatus } from '../tool-card/tool-card.component';
import { topRatedSymptom, toolsForSymptom, RatedSymptom } from '../tool-reason';
import { RestService } from '../../services/rest.service';
import { DismissedToolsService } from '../dismissed-tools.service';
import { TranslationService } from '../../services/translation.service';
import { SessionService, isEntitled } from '../../services/session.service';
import { RoutineService } from '../../services/routine.service';
import { CompletionService } from '../../services/completion.service';
import { SyncStore } from '../../services/sync-store.service';
import {
  Completion,
  INSIGHT_COUNT,
  RoutineSchedule,
  TOOL_BY_ID,
  TOOLS,
  toolImg,
} from '../shell.models';
import { formatFirstNameForDisplay } from '../../shared/name-display';
import { AppDownloadCardComponent } from '../../shared/components/app-download/app-download-card.component';
import { AppSettingsService } from '../../services/app-settings.service';
import { markHomeShownOnDevice } from '../home-shown';
import { StoreBillingService } from '../../services/store-billing.service';

interface Card {
  id: string;
  name: string;
  color: string;
  done: boolean;
  /** B12: short subtitle (reuses the existing per-tool description copy). */
  desc: string;
  /** B12: typical minutes for one round. */
  mins?: number;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, TranslatePipe, AppDownloadCardComponent, ToolCardComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private translation = inject(TranslationService);
  private session = inject(SessionService);
  private rest = inject(RestService);
  private dismissedSvc = inject(DismissedToolsService);
  private routineSvc = inject(RoutineService);
  private completionSvc = inject(CompletionService);
  private sync = inject(SyncStore);
  private appSettings = inject(AppSettingsService);
  private storeBilling = inject(StoreBillingService);

  loading = true;
  name = '';
  greeting = '';

  // One-time-per-session nudge on the "start a new scan" card icon (Danna: the
  // pulse belongs on the purple face icon here, not the bottom nav). Plays on the
  // first scroll (she's looking then) or a short fallback; reduced-motion safe.
  scanNudge = false;
  private scanNudgeArmed = false;
  private scanScrollHandler?: (e: Event) => void;
  private scanNudgeFallback?: ReturnType<typeof setTimeout>;
  private scanNudgeEnd?: ReturnType<typeof setTimeout>;
  subtitle = '';
  streak = 0;
  showStreakBadge = false;

  // engagement
  engPct = 0;
  engReached = false;
  engMsg = '';
  engActive = 0;
  engTarget = 16;

  insight = '';

  todayCards: Card[] = [];
  remaining: Card[] = [];
  doneCount = 0;
  planTotal = 0;
  allDone = false;
  hasPlan = false;

  moreTools: {
    id: string;
    name: string;
    color: string;
    desc: string;
    /** One line saying why this tool is for HER. Empty when we cannot say. */
    why: string;
  }[] = [];
  /** Her highest-rated symptom, the thing a reason line can point at. */
  private topSymptomId = '';
  /** Every symptom she rated above zero, highest first. Picks "next up". */
  private ratedDesc: { id: string; value: number }[] = [];
  /** The schedule the strip was last built from, so it can be rebuilt. */
  private lastSchedule: RoutineSchedule | null = null;
  selectedMoreTools = new Set<string>();
  isAddingTools = false;

  // §wellness — the body metrics (water/protein/fiber/energy/movement). Danna
  // 22.09 (part 2, item c): they no longer stand as a section of their own with
  // a single item in it; they ride in today's task row as ordinary cards.
  wellnessCards: Card[] = [];

  // §flower — animated canvas bloom that grows with the day's completion
  // (faithful port of canonical startProgressFlower). Replaces the old ring.
  @ViewChild('flowerCanvas') flowerCanvasRef?: ElementRef<HTMLCanvasElement>;
  private flowerCtx: CanvasRenderingContext2D | null = null;
  private flowerAnimId: number | null = null;
  private flowerTime = 0;
  private flowerPct = 0;
  private flowerBloom = false;
  private flowerStarted = false;
  private flowerPetalDefs: any[] = [];
  private flowerSparkles: any[] = [];
  private viewReady = false;

  // §4: previous results reachable from home without redoing the scan.
  prevScanGuid: string | null = null;
  /** Days until her next mapping: the admin interval (server setting, never a
   *  literal) minus the days since her latest completed scan. Null = no line
   *  (no finished scan yet, or the next one is already due). */
  nextScanDays: number | null = null;
  // The FIRST (free) scan — the "הסקירה הראשונית שלך" card opens THIS, never the
  // latest (which may be a locked repeat that would bounce to the paywall).
  firstScanGuid: string | null = null;

  // --- Locked-flow / paywall state (spec §2/§3) -----------------------------
  // The unpaid user who has completed her first scan sees the upgrade upsell.
  // freeTierToolsLocked === true => Option A (tools locked, engagement zones
  // removed); false/null => Option B (tools open, zones kept — default).
  get isUnpaid(): boolean {
    // Unified entitlement: admins + admin-granted-credit users are treated as
    // paid here too, so they don't see the upsell banner or locked tool tiles.
    return !isEntitled(this.session.session?.user);
  }
  get hasCompletedScan(): boolean {
    return !!this.prevScanGuid;
  }
  get showUpsell(): boolean {
    return this.isUnpaid && this.hasCompletedScan;
  }
  get toolsLocked(): boolean {
    return this.isUnpaid && !!this.session.session?.user?.freeTierToolsLocked;
  }
  get upsellBodyKey(): string {
    return this.toolsLocked
      ? 'shell.home.upsell.bodyLocked'
      : 'shell.home.upsell.bodyOpen';
  }

  // Open the consumer paywall ("התמונה המלאה"). The paywall needs a scanId; use
  // the latest completed scan so the one-time pass / subscription flow has context.
  openPaywall(): void {
    this.router.navigate(['/' + this.lang() + '/app/payment'], {
      queryParams: { scanId: this.prevScanGuid || undefined },
    });
  }

  // Returning from PayPal subscription approval: /app/home?subscription=success&subscription_id=...
  // Confirm with the server (it checks PayPal + sets paidUntil), which updates the session so the UI
  // flips to paid. Strips the query params so a refresh won't re-trigger.
  private async handleSubscriptionReturn(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription') !== 'success') return;
    const subId = params.get('subscription_id');
    try {
      if (subId) await this.session.confirmSubscriptionReturn(subId);
    } finally {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  async ngOnInit(): Promise<void> {
    // Surface split A1: this device has now shown home, so later sign-ins
    // here land on home rather than on her results.
    markHomeShownOnDevice();
    // Returning from a PayPal subscription approval (/app/home?subscription=success&subscription_id=)
    // → confirm it synchronously and flip to paid (the webhook is async + lands after this redirect).
    await this.handleSubscriptionReturn();

    this.name = formatFirstNameForDisplay(this.session.session?.user?.fname);
    this.buildGreeting();

    // Re-read the account in the background: entitlement can change AFTER the
    // login that built the cached session (a clinic granting free tests when she
    // joined, an allowance raised later), and until it was re-read home kept
    // showing the locked cards and sent her to the paywall she had already been
    // given access past. Never blocks the paint.
    void this.session.refreshUser();
    // Native apps: re-link any App Store / Google Play purchase (a renewal, or a
    // plan bought on another device) to this account. Quiet, never blocks.
    if (this.storeBilling.enabled) void this.storeBilling.restore(false);

    // Paint synchronously from the persisted snapshot when we have one (instant,
    // no blank screen / loader on cold start), then revalidate in the background
    // and update the numbers when fresh data arrives. On a fresh install with no
    // snapshot, fall back to a normal fetch-then-paint.
    const schedule = this.routineSvc.peek();
    const completions = this.completionSvc.peek();
    if (schedule && completions) {
      this.paint(schedule, completions);
      this.fetchData(true)
        .then(([s, c]) => this.paint(s, c))
        .catch((e) => console.error('HOME refresh failed:', e)); // keep cached
    } else {
      try {
        const [s, c] = await this.fetchData(false);
        this.paint(s, c);
      } catch (e) {
        console.error('HOME load failed:', e);
        this.paint({}, []); // empty state, never a permanent blank
      }
    }

    this.loadPrevious();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.ensureFlower();
    this.armScanNudge();
  }

  ngOnDestroy(): void {
    this.stopFlowerAnim();
    this.teardownScanNudge();
    if (this.scanNudgeEnd) clearTimeout(this.scanNudgeEnd);
  }

  /** Arm the once-per-session scan-card nudge: play on the first scroll or a
   *  short fallback. sessionStorage gate keeps it to a single play; reduced
   *  motion skips it. Capture-phase scroll listener so inner scrollers count. */
  private armScanNudge(): void {
    if (this.scanNudgeArmed || typeof window === 'undefined') return;
    this.scanNudgeArmed = true;
    try {
      if (sessionStorage.getItem('newscan_nudged')) return;
      sessionStorage.setItem('newscan_nudged', '1');
    } catch {}
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const play = () => {
      this.teardownScanNudge();
      this.playScanNudge();
    };
    this.scanScrollHandler = (e: Event) => {
      const t = e.target as any;
      const top =
        t && t !== document && t !== window
          ? t.scrollTop ?? 0
          : window.scrollY || document.documentElement.scrollTop || 0;
      if (top > 16) play();
    };
    window.addEventListener('scroll', this.scanScrollHandler, true);
    this.scanNudgeFallback = setTimeout(play, 2000);
  }

  private teardownScanNudge(): void {
    if (this.scanScrollHandler) {
      window.removeEventListener('scroll', this.scanScrollHandler, true);
      this.scanScrollHandler = undefined;
    }
    if (this.scanNudgeFallback) {
      clearTimeout(this.scanNudgeFallback);
      this.scanNudgeFallback = undefined;
    }
  }

  private playScanNudge(): void {
    this.scanNudge = true;
    this.scanNudgeEnd = setTimeout(() => (this.scanNudge = false), 3800);
  }

  // ---- progress flower (animated canvas bloom) ----

  private initFlowerDefs(): void {
    if (this.flowerSparkles.length) return;
    for (let i = 0; i < 40; i++) {
      this.flowerSparkles.push({
        angle: Math.random() * Math.PI * 2,
        dist: 25 + Math.random() * 65,
        size: 0.5 + Math.random() * 2,
        speed: 0.3 + Math.random() * 0.7,
        phase: Math.random() * Math.PI * 2,
        brightness: 0.3 + Math.random() * 0.7,
      });
    }
    for (let j = 0; j < 10; j++) {
      this.flowerPetalDefs.push({
        baseAngle: (j / 10) * 360,
        swayAmp: 1.5 + Math.random() * 3,
        swaySpeed: 3.5 + Math.random() * 2.5,
        swayPhase: Math.random() * Math.PI * 2,
        widthMult: 0.85 + Math.random() * 0.3,
        wavyOffset1: -5 + Math.random() * 10,
        wavyOffset2: -5 + Math.random() * 10,
        wavyOffset3: -3 + Math.random() * 6,
        zOrder: j < 5 ? 0 : 1,
      });
    }
    this.flowerPetalDefs.sort((a, b) => a.zOrder - b.zOrder);
  }

  // start (or refresh) the bloom for the current completion ratio
  private ensureFlower(): void {
    if (!this.viewReady || !this.hasPlan) return;
    const canvas = this.flowerCanvasRef?.nativeElement;
    if (!canvas) {
      // canvas mounts with the plan; retry on the next frame
      if (this.viewReady && this.hasPlan) setTimeout(() => this.ensureFlower(), 60);
      return;
    }
    this.flowerPct = this.planTotal ? this.doneCount / this.planTotal : 0;
    this.flowerBloom = this.allDone;
    if (this.flowerStarted) return; // loop already running; reads pct/bloom live
    this.initFlowerDefs();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const DPR = window.devicePixelRatio || 1;
    // Wider than it is tall now: at full bloom the side petals reach further
    // than the old 200-wide canvas allowed, so they were about to be cut off.
    const LW = 300,
      LH = 280;
    canvas.width = LW * DPR;
    canvas.height = LH * DPR;
    // Danna 22.09 (part 2, item b): the bloom is no longer half a screen of its
    // own - it is drawn at full size and shown at icon size inside the counter
    // row, so it keeps its detail on a retina screen. The CSS box owns the size.
    canvas.style.width = '';
    canvas.style.height = '';
    ctx.scale(DPR, DPR);
    this.flowerCtx = ctx;
    this.flowerTime = 0;
    this.flowerStarted = true;
    this.drawFlower(LW, LH);
  }

  private stopFlowerAnim(): void {
    if (this.flowerAnimId) {
      cancelAnimationFrame(this.flowerAnimId);
      this.flowerAnimId = null;
    }
    this.flowerStarted = false;
  }

  private drawFlower(LW: number, LH: number): void {
    const ctx = this.flowerCtx;
    if (!ctx) return;
    // Danna 22.09, third enlargement. The bloom sits at the centre of a canvas
    // sized so that the furthest a petal can reach (petalLen * widthMult * 1.02
    // = 141) clears EVERY edge, upward and sideways alike; the stem and leaves
    // below are fractions of the remaining stem, so moving the centre can never
    // make the stem overshoot its own end.
    const CX = 150,
      CY = 150;
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    let wasFullBloom = false,
      celebrationTime = -1;

    const frame = () => {
      this.flowerTime += 1 / 60;
      const t = this.flowerTime;
      const pct = this.flowerPct;
      const isFullBloom = this.flowerBloom;
      if (isFullBloom && !wasFullBloom) {
        celebrationTime = t;
        wasFullBloom = true;
      }
      if (!isFullBloom) {
        wasFullBloom = false;
        celebrationTime = -1;
      }
      let celebScale = 1;
      if (celebrationTime > 0) {
        const ct = t - celebrationTime;
        if (ct < 1.5) celebScale = 1 + 0.08 * Math.sin((ct / 1.5) * Math.PI) * Math.max(0, 1 - ct / 1.5);
      }

      ctx.clearRect(0, 0, LW, LH);

      const bgGrad = ctx.createRadialGradient(CX, CY, 0, CX, CY, 100);
      bgGrad.addColorStop(0, 'rgba(26,10,46,0.12)');
      bgGrad.addColorStop(0.45, 'rgba(26,10,46,0.05)');
      bgGrad.addColorStop(1, 'rgba(26,10,46,0)');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, LW, LH);

      const glowI = 0.05 + pct * 0.55;
      const glowP = 0.7 + 0.3 * Math.sin(t * 1.2);
      const igA = glowI * glowP;
      const ig = ctx.createRadialGradient(CX, CY - 6, 0, CX, CY - 6, 60 * (0.3 + pct * 0.7));
      ig.addColorStop(0, 'rgba(255,180,80,' + igA * 0.6 + ')');
      ig.addColorStop(0.3, 'rgba(255,100,150,' + igA * 0.3 + ')');
      ig.addColorStop(0.7, 'rgba(255,60,120,' + igA * 0.1 + ')');
      ig.addColorStop(1, 'rgba(255,60,120,0)');
      ctx.fillStyle = ig;
      ctx.fillRect(0, 0, LW, LH);

      ctx.save();
      ctx.translate(CX, CY);
      ctx.scale(celebScale, celebScale);
      ctx.translate(-CX, -CY);

      // stem
      const sw = Math.sin(t * 0.8) * 1.5;
      ctx.save();
      ctx.translate(CX, CY + 10);
      ctx.rotate((sw * Math.PI) / 180);
      ctx.translate(-CX, -(CY + 10));
      // the stem runs from just under the bloom to the foot of the canvas
      const stemTop = CY + 10;
      const stemLen = LH - 15 - stemTop;
      const sp = (f: number) => stemTop + stemLen * f;
      ctx.beginPath();
      ctx.moveTo(CX, stemTop);
      ctx.bezierCurveTo(CX - 3, sp(0.25), CX + 4, sp(0.54), CX + 1, sp(0.83));
      ctx.bezierCurveTo(CX - 1, sp(0.96), CX + 1, sp(1.04), CX, LH - 15);
      ctx.strokeStyle = 'rgba(100,80,60,0.5)';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.stroke();
      if (pct > 0.15) {
        ctx.save();
        const lsz = 14 * Math.min(1, pct * 2);
        const ls1 = Math.sin(t * 0.9) * 3;
        ctx.translate(CX - 1, sp(0.375));
        ctx.rotate(((-35 + ls1) * Math.PI) / 180);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.bezierCurveTo(-lsz * 0.4, -lsz * 0.3, -lsz * 0.5, -lsz * 0.7, 0, -lsz);
        ctx.bezierCurveTo(lsz * 0.5, -lsz * 0.7, lsz * 0.4, -lsz * 0.3, 0, 0);
        ctx.fillStyle = 'rgba(100,160,80,0.35)';
        ctx.fill();
        ctx.restore();
      }
      if (pct > 0.35) {
        ctx.save();
        const rsz = 12 * Math.min(1, pct * 1.5);
        const ls2 = Math.sin(t * 0.7 + 1) * 3;
        ctx.translate(CX + 2, sp(0.567));
        ctx.rotate(((30 + ls2) * Math.PI) / 180);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.bezierCurveTo(-rsz * 0.4, -rsz * 0.3, -rsz * 0.5, -rsz * 0.7, 0, -rsz);
        ctx.bezierCurveTo(rsz * 0.5, -rsz * 0.7, rsz * 0.4, -rsz * 0.3, 0, 0);
        ctx.fillStyle = 'rgba(100,160,80,0.35)';
        ctx.fill();
        ctx.restore();
      }
      ctx.restore();

      // petals
      const visiblePetals = Math.max(0, Math.round(3 + pct * 7));
      // Danna 22.09: longer petals and colours that carry. The tip sits at
      // CY - petalLen * widthMult (max 1.15), so 79 keeps a full bloom just
      // inside the 240-high canvas instead of clipping at the top.
      const petalLen = 44 + pct * 76;
      const ci = 0.15 + pct * 0.85;
      for (let i = 0; i < this.flowerPetalDefs.length; i++) {
        if (i >= visiblePetals) break;
        const pd = this.flowerPetalDefs[i];
        const psway = pd.swayAmp * Math.sin((t / pd.swaySpeed) * Math.PI * 2 + pd.swayPhase);
        const isBack = pd.zOrder === 0;
        const petalOpacity = isBack ? 0.45 + ci * 0.4 : 0.6 + ci * 0.4;
        ctx.save();
        ctx.translate(CX, CY);
        ctx.rotate(((pd.baseAngle + psway) * Math.PI) / 180);
        const L = petalLen * pd.widthMult,
          W = L * 0.38;
        ctx.beginPath();
        ctx.moveTo(0, -5);
        ctx.bezierCurveTo(-W * 0.4 + pd.wavyOffset1, -L * 0.25, -W * 0.95 + pd.wavyOffset2, -L * 0.55, -W * 0.7 + pd.wavyOffset3, -L * 0.8);
        ctx.bezierCurveTo(-W * 0.45, -L * 0.95, -W * 0.15, -L * 1.02, 0, -L);
        ctx.bezierCurveTo(W * 0.15, -L * 1.02, W * 0.45, -L * 0.95, W * 0.7 - pd.wavyOffset3, -L * 0.8);
        ctx.bezierCurveTo(W * 0.95 - pd.wavyOffset2, -L * 0.55, W * 0.4 - pd.wavyOffset1, -L * 0.25, 0, -5);
        ctx.closePath();
        const g1 = Math.round(lerp(200, 0, ci)),
          b1 = Math.round(lerp(220, 128, ci));
        const g2 = Math.round(lerp(220, 77, ci)),
          b2 = Math.round(lerp(200, 148, ci));
        const g3 = Math.round(lerp(240, 179, ci)),
          b3 = Math.round(lerp(210, 71, ci));
        const g4 = Math.round(lerp(250, 215, ci)),
          b4 = Math.round(lerp(220, 0, ci));
        const grad = ctx.createLinearGradient(0, -5, 0, -L);
        // The old stops faded to 0.3 alpha at the tip, which at icon size read
        // as a grey smudge. The petal now keeps its colour to the end.
        grad.addColorStop(0, 'rgba(255,' + g1 + ',' + b1 + ',' + petalOpacity + ')');
        grad.addColorStop(0.35, 'rgba(255,' + g2 + ',' + b2 + ',' + petalOpacity * 0.92 + ')');
        grad.addColorStop(0.65, 'rgba(255,' + g3 + ',' + b3 + ',' + petalOpacity * 0.82 + ')');
        grad.addColorStop(1, 'rgba(255,' + g4 + ',' + b4 + ',' + petalOpacity * 0.7 + ')');
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.save();
        ctx.shadowColor = 'rgba(255,100,60,' + (0.1 + pct * 0.2) + ')';
        ctx.shadowBlur = 8 + pct * 12;
        ctx.fill();
        ctx.restore();
        const vA = 0.2 + pct * 0.5;
        ctx.beginPath();
        ctx.moveTo(0, -10);
        ctx.bezierCurveTo(-1.5, -L * 0.35, 1, -L * 0.6, 0, -L * 0.88);
        ctx.strokeStyle = 'rgba(255,200,100,' + vA + ')';
        ctx.lineWidth = 0.8;
        ctx.stroke();
        ctx.restore();
      }

      // center
      const cgP = 0.7 + 0.3 * Math.sin(t * 1.8);
      const cga = 0.15 + pct * 0.45;
      const cg = ctx.createRadialGradient(CX, CY, 0, CX, CY, 16);
      cg.addColorStop(0, 'rgba(255,200,100,' + cga * cgP + ')');
      cg.addColorStop(0.4, 'rgba(255,120,80,' + cga * cgP * 0.5 + ')');
      cg.addColorStop(1, 'rgba(255,80,120,0)');
      ctx.fillStyle = cg;
      ctx.beginPath();
      ctx.arc(CX, CY, 16, 0, Math.PI * 2);
      ctx.fill();
      const core = ctx.createRadialGradient(CX, CY, 0, CX, CY, 5 + pct * 2);
      core.addColorStop(0, 'rgba(255,' + Math.round(lerp(220, 160, ci)) + ',' + Math.round(lerp(200, 80, ci)) + ',0.9)');
      core.addColorStop(0.6, 'rgba(255,' + Math.round(lerp(200, 80, ci)) + ',' + Math.round(lerp(180, 100, ci)) + ',0.7)');
      core.addColorStop(1, 'rgba(200,' + Math.round(lerp(160, 40, ci)) + ',' + Math.round(lerp(140, 80, ci)) + ',0.3)');
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(CX, CY, 5 + pct * 2 + Math.sin(t * 2) * 0.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // sparkles
      const visCount = isFullBloom ? 40 : Math.floor(pct * 25);
      for (let si = 0; si < visCount && si < this.flowerSparkles.length; si++) {
        const sp = this.flowerSparkles[si];
        const st = t * sp.speed + sp.phase;
        const twinkle = Math.sin(st * 2.5) * 0.5 + 0.5;
        if (twinkle < 0.15) continue;
        const sdist = sp.dist * (0.3 + pct * 0.7);
        const sangle = sp.angle + Math.sin(st * 0.3) * 0.15;
        const sx = CX + Math.cos(sangle) * sdist;
        const sy = CY + Math.sin(sangle) * sdist - 6;
        const salpha = twinkle * sp.brightness * (0.3 + pct * 0.7);
        const ssize = sp.size * (0.5 + twinkle * 0.5);
        const sg = ctx.createRadialGradient(sx, sy, 0, sx, sy, ssize * 3);
        sg.addColorStop(0, 'rgba(255,240,200,' + salpha + ')');
        sg.addColorStop(0.5, 'rgba(255,200,150,' + salpha * 0.3 + ')');
        sg.addColorStop(1, 'rgba(255,180,120,0)');
        ctx.fillStyle = sg;
        ctx.beginPath();
        ctx.arc(sx, sy, ssize * 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(sx, sy, ssize * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,' + salpha * 0.9 + ')';
        ctx.fill();
      }

      this.flowerAnimId = requestAnimationFrame(frame);
    };
    frame();
  }

  // Surface split C2: "Your next scan in {{days}} days". The interval is the
  // admin setting; the count runs from her latest completed scan.
  private async loadNextScanDays(created: string | null): Promise<void> {
    if (!created) {
      this.nextScanDays = null;
      return;
    }
    const { scanIntervalDays } = await this.appSettings.get();
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const elapsed = Math.round((startOf(new Date()) - startOf(new Date(created))) / 86400000);
    const left = scanIntervalDays - elapsed;
    this.nextScanDays = left > 0 ? left : null;
  }

  // Look up the woman's latest completed scan so home can offer "view previous
  // results" (§4) — non-blocking; home renders fine without it.
  private loadPrevious(): void {
    const uid = this.session.session?.user?.id;
    if (!uid) return;
    this.session
      .getLatestCompletedScan(uid)
      .then(async (latest) => {
        const g = latest?.guid ?? null;
        this.prevScanGuid = g;
        void this.loadNextScanDays(latest?.created ?? null);
        // Her ratings decide which tools may state a reason, so the strip is
        // rebuilt once they land. Non-blocking: home already painted.
        await this.loadTopSymptom();
        if (this.topSymptomId && this.lastSchedule) {
          this.buildMoreTools(this.lastSchedule);
        }
      })
      .catch(() => {});
    this.session
      .getFirstCompletedScanGuid(uid)
      .then((g) => (this.firstScanGuid = g))
      .catch(() => {});
  }

  private fetchData(force: boolean): Promise<[RoutineSchedule, Completion[]]> {
    return Promise.all([
      this.routineSvc.getSchedule(force),
      this.completionSvc.getRange(undefined, undefined, force),
    ]);
  }

  // Pure, synchronous render of all view state from a data snapshot — safe to
  // call repeatedly (instant cache paint, then again when fresh data lands).
  private paint(schedule: RoutineSchedule, completions: Completion[]): void {
    const u = this.session.session?.user;
    this.buildEngagement(completions);
    this.buildInsight(u?.created);
    this.buildPlan(schedule, completions);
    this.buildSubtitle(completions);
    this.buildMoreTools(schedule);
    this.loading = false;
  }

  private t(key: string, opts?: any): string {
    return this.translation.translate(key, opts);
  }

  private buildGreeting(): void {
    const h = new Date().getHours();
    let key = 'night';
    if (h >= 5 && h < 12) key = 'morning';
    else if (h >= 12 && h < 17) key = 'afternoon';
    else if (h >= 17 && h < 21) key = 'evening';
    const base = this.t('shell.greet.' + key);
    this.greeting = this.name ? `${base}, ${this.name}` : base;
  }

  private buildEngagement(completions: Completion[]): void {
    const e = this.completionSvc.engagement(completions);
    this.engPct = e.pct;
    this.engReached = e.reached;
    this.engActive = e.activeDays;
    this.engTarget = e.target;
    this.streak = this.completionSvc.streak(completions);
    this.showStreakBadge = this.streak >= 2;

    const needed = Math.max(0, e.target - e.activeDays);
    if (e.reached) this.engMsg = this.t('shell.engagement.reached');
    else if (e.activeDays <= 1) this.engMsg = this.t('shell.engagement.start');
    else if (needed <= 3)
      this.engMsg = this.t('shell.engagement.near', { n: needed });
    else if (e.activeDays >= 12)
      this.engMsg = this.t('shell.engagement.mid', {
        n: e.activeDays,
        target: e.target,
      });
    else
      this.engMsg = this.t('shell.engagement.default', {
        n: e.activeDays,
        target: e.target,
      });

  }

  private buildInsight(created?: string): void {
    let idx = 0;
    if (created) {
      const days = Math.floor(
        (Date.now() - new Date(created).getTime()) / 86400000,
      );
      idx = ((days % INSIGHT_COUNT) + INSIGHT_COUNT) % INSIGHT_COUNT;
    } else {
      const doy = Math.floor(
        (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) /
          86400000,
      );
      idx = doy % INSIGHT_COUNT;
    }
    this.insight = this.t('shell.insights.' + idx);
  }

  /** B12: the one card the dashboard makes dominant - her next action. Danna
   *  26.09: it is the tool that answers the symptom she rated highest. Her
   *  ratings are walked top down, so when the top one has no matching tool
   *  still waiting today the next rated symptom decides. With no match at all
   *  (or no ratings) it falls back to the first tool in today's plan. Null
   *  once everything is done. */
  get nextCard(): Card | null {
    const waiting = this.todayCards.filter((c) => !c.done);
    for (const r of this.ratedDesc) {
      for (const toolId of toolsForSymptom(r.id)) {
        const hit = waiting.find((c) => c.id === toolId);
        if (hit) return hit;
      }
    }
    return waiting[0] || null;
  }

  /** The strip under the dominant card. Danna 22.09 (part 1, item 3): the tool
   *  the big card already shows must not appear again as the first tile, so the
   *  row starts at the task AFTER it. */
  get restCards(): Card[] {
    const next = this.nextCard;
    return this.todayCards.filter((c) => c !== next);
  }

  /** B12: the tool's short subtitle. Reuses the existing results catalogue copy
   *  (results.tools.items.<id>.desc); blank when a tool has no description, so a
   *  raw i18n key can never leak onto the dashboard. */
  /**
   * She said no thanks. The tile does NOT vanish under her finger (Danna
   * 22.09, "the tool just disappears"): it stays where it is, quietened, and
   * offers an undo. It simply is not offered again on the next visit.
   */
  dismissTool(id: string): void {
    this.dismissedSvc.dismiss(id);
    this.selectedMoreTools.delete(id);
  }

  /** She changed her mind, in the same breath. */
  undoDismissTool(id: string): void {
    this.dismissedSvc.restore(id);
  }

  isDismissed(id: string): boolean {
    return this.dismissedSvc.has(id);
  }

  /** Her rated symptoms, so a recommendation can name the reason. */
  private async loadTopSymptom(): Promise<void> {
    try {
      const guid =
        this.prevScanGuid ||
        (await this.session.getLatestCompletedScanGuid(
          this.session.session?.user?.id as number,
        ));
      if (!guid) return;
      const res = await this.rest.request<{ data?: RatedSymptom[] }>({
        method: 'GET',
        path: `doctor-report/${guid}/rated-symptoms`,
        throwError: true,
      });
      const top = topRatedSymptom(res?.data);
      if (top) this.topSymptomId = top.id;
      this.ratedDesc = (Array.isArray(res?.data) ? res!.data! : [])
        .map((r) => ({ id: r?.key, value: Number(r?.value) }))
        .filter((r) => typeof r.id === 'string' && r.value > 0)
        .sort((a, b) => b.value - a.value);
    } catch {
      /* no ratings, no reason line. The strip still renders. */
    }
  }

  /** The status the shared card shows, same words as everywhere else. */
  cardStatus(done: boolean): ToolCardStatus {
    return done ? 'done' : 'todo';
  }

  private toolDesc(id: string): string {
    const key = 'results.tools.items.' + id + '.desc';
    const value = this.t(key);
    return value && value !== key ? value : '';
  }

  private buildPlan(schedule: RoutineSchedule, completions: Completion[]): void {
    const planIds = this.routineSvc.getTodayPlan(schedule);
    const done = this.completionSvc.todayDoneSet(completions);
    this.hasPlan = planIds.length > 0;

    this.todayCards = planIds.map((id) => ({
      id,
      name: this.t('shell.tools.' + id + '.name'),
      color: TOOL_BY_ID[id]?.color || '#ccc',
      done: done.has(id),
      desc: this.toolDesc(id),
      mins: TOOL_BY_ID[id]?.mins,
    }));
    this.buildWellness(planIds);
    this.todayCards = this.todayCards.concat(this.wellnessCards);
    this.remaining = this.todayCards.filter((c) => !c.done);

    // The counter row sits directly under the task strip and says what it
    // counts, so it has to count exactly what the strip SHOWS - the body-metric
    // cards folded in (item c) included, not the routine alone. The bloom reads
    // the same two numbers.
    this.planTotal = this.todayCards.length;
    this.doneCount = this.todayCards.filter((c) => c.done).length;
    this.allDone = this.hasPlan && this.doneCount >= this.planTotal;

    this.ensureFlower();
  }

  // The body metrics for today, read from the same localStorage day-buckets the
  // tools write (ph_data/pp_log/pt_steps). One card per TOOL, so the three meal
  // metrics never become three identical cards, and only for tools that are not
  // already in today's plan (otherwise the same tile would show up twice).
  private buildWellness(planIds: string[]): void {
    const hasPlan = (id: string) => planIds.includes(id);
    const today = this.completionSvc.today();
    const readJson = (k: string): any => {
      try {
        const raw = localStorage.getItem(k);
        const v = raw ? JSON.parse(raw) : {};
        return v && typeof v === 'object' ? v : {};
      } catch {
        return {};
      }
    };
    const water = readJson('ph_data')[today]?.w || 0;
    // The daily meal log lives in pp_log (pp_meals = reusable templates); each
    // entry carries macro totals (protein_g / fiber_g), NOT the canonical
    // prot/fiber/gl field names — read the real fields or the panel stays at 0.
    const meals: any[] = readJson('pp_log')[today] || [];
    let prot = 0,
      fiber = 0;
    for (const m of meals) {
      const tot = m?.totals || m || {};
      prot += tot.protein_g || 0;
      fiber += tot.fiber_g || 0;
    }
    const steps = readJson('pt_steps')[today] || 0;
    // "steady energy" proxy: a logged day with protein + some fiber blunts the
    // glucose spike (canonical used glycemic load; approximated from real macros).
    const energy = prot > 0 && fiber >= 5 ? 1 : 0;
    const hasWaterData = water > 0;
    const hasMealData = meals.length > 0 || prot > 0 || fiber > 0;
    const hasStepsData = steps > 0;

    const all = [
      { key: 'water', show: hasPlan('water') || hasWaterData, tool: 'water', val: water, goal: 2000 },
      { key: 'protein', show: hasPlan('meal') || hasMealData, tool: 'meal', val: prot, goal: 110 },
      { key: 'fiber', show: hasPlan('meal') || hasMealData, tool: 'meal', val: fiber, goal: 28 },
      { key: 'energy', show: hasPlan('meal') || hasMealData, tool: 'meal', val: energy, goal: 1 },
      { key: 'movement', show: hasPlan('steps') || hasStepsData, tool: 'steps', val: steps, goal: 6000 },
    ];
    // A tool already in today's plan has its own card in the row already.
    const shown = all.filter((m) => m.show && !hasPlan(m.tool));
    const byTool = new Map<string, { key: string; filled: boolean }[]>();
    for (const m of shown) {
      const list = byTool.get(m.tool) || [];
      list.push({ key: m.key, filled: m.val >= m.goal });
      byTool.set(m.tool, list);
    }
    this.wellnessCards = [...byTool.entries()].map(([tool, metrics]) => ({
      id: tool,
      // A tool carrying a single metric is named by that metric ("Hydration"),
      // which is what she knows it by; a tool carrying several keeps its own name.
      name:
        metrics.length === 1
          ? this.t('shell.home.wellness.' + metrics[0].key)
          : this.t('shell.tools.' + tool + '.name'),
      color: TOOL_BY_ID[tool]?.color || '#ccc',
      done: metrics.every((m) => m.filled),
      desc: this.toolDesc(tool),
      mins: TOOL_BY_ID[tool]?.mins,
    }));
  }

  private buildSubtitle(completions: Completion[]): void {
    const daysSince = this.completionSvc.daysSinceLast(completions);
    const everActive = completions.length > 0;
    let key: string;

    const firstDayShown = localStorage.getItem('pt_sub_firstday');
    if (!everActive && !firstDayShown) {
      this.sync.setItem('pt_sub_firstday', '1');
      key = 'first_day';
    } else if (daysSince !== null && daysSince >= 3) {
      key = 'comeback';
    } else if (this.streak >= 30) key = 'streak_30';
    else if (this.streak >= 14) key = 'streak_14';
    else if (this.streak >= 7) key = 'streak_7';
    else if (this.allDone) key = 'all_done';
    else if (this.doneCount > 0) key = 'in_progress';
    else key = 'returning_idle';

    this.subtitle = this.t('shell.sub.' + key);
  }

  private buildMoreTools(schedule: RoutineSchedule): void {
    this.lastSchedule = schedule;
    const planSet = new Set(this.routineSvc.getTodayPlan(schedule));
    // A tool she declined is not offered again, and the ones that answer her
    // top symptom are the ones that get to say why.
    const forHer = new Set(
      this.topSymptomId ? toolsForSymptom(this.topSymptomId) : [],
    );
    const symptomName = this.topSymptomId
      ? this.t('doctorReport.symptoms.' + this.topSymptomId)
      : '';
    const haveName = !!symptomName && !symptomName.startsWith('doctorReport.');

    this.moreTools = TOOLS.filter(
      (t) => !planSet.has(t.id) && !this.dismissedSvc.has(t.id),
    )
      // the ones that speak to her top symptom come first
      .sort((a, b) => Number(forHer.has(b.id)) - Number(forHer.has(a.id)))
      .slice(0, 8)
      .map((t) => ({
        id: t.id,
        name: this.t('shell.tools.' + t.id + '.name'),
        color: t.color,
        desc: this.toolDesc(t.id),
        why:
          forHer.has(t.id) && haveName
            ? this.t('shell.home.rec.because', { symptom: symptomName })
            : '',
      }));
  }

  // Edit mode (Danna 26.09): take tools out of her routine from home. The list
  // is her WHOLE active routine (not only what is due now), frozen when she
  // opens it so nothing jumps under her finger. Each tap saves at once; the
  // page is rebuilt only on Done, so a removed tool stays visible (greyed) and
  // one more tap brings it back.
  editing = false;
  editCards: { id: string; name: string }[] = [];
  editRemoved = new Set<string>();
  editBusy = new Set<string>();

  async startEdit(): Promise<void> {
    let schedule = this.lastSchedule || this.routineSvc.peek() || {};
    try {
      schedule = await this.routineSvc.getSchedule(true);
    } catch {
      /* offline: edit what we have */
    }
    this.editCards = Object.keys(schedule)
      .filter((id) => schedule[id]?.active && TOOL_BY_ID[id])
      .map((id) => ({ id, name: this.t('shell.tools.' + id + '.name') }));
    this.editRemoved.clear();
    this.editing = true;
  }

  async toggleRemove(id: string): Promise<void> {
    if (this.editBusy.has(id)) return;
    this.editBusy.add(id);
    const removing = !this.editRemoved.has(id);
    try {
      // Always from a fresh read, so a change made elsewhere is never overwritten.
      const schedule: RoutineSchedule = { ...(await this.routineSvc.getSchedule(true)) };
      const cur = schedule[id] || { active: true, freq: 'daily', time: 'anytime' };
      schedule[id] = { ...cur, active: !removing };
      await this.routineSvc.saveSchedule(schedule);
      if (removing) this.editRemoved.add(id);
      else this.editRemoved.delete(id);
    } catch (e) {
      console.error('HOME edit routine failed:', e);
    } finally {
      this.editBusy.delete(id);
    }
  }

  async finishEdit(): Promise<void> {
    if (this.editBusy.size) return;
    this.editing = false;
    this.editRemoved.clear();
    try {
      const [s, c] = await this.fetchData(true);
      this.paint(s, c);
    } catch {
      /* offline: the next visit repaints from the server */
    }
  }

  // "עוד כלים" → select tools, then "הוסיפי את הכלים שבחרתי" adds them to the
  // daily routine (active, anytime/daily so they immediately appear under
  // "מה שנשאר להיום" and drop out of "עוד כלים" on the re-paint).
  toggleToolSelection(id: string): void {
    if (this.selectedMoreTools.has(id)) this.selectedMoreTools.delete(id);
    else this.selectedMoreTools.add(id);
  }

  async addSelectedTools(): Promise<void> {
    if (this.isAddingTools || !this.selectedMoreTools.size) return;
    this.isAddingTools = true;
    try {
      const schedule: RoutineSchedule = { ...(await this.routineSvc.getSchedule(true)) };
      this.selectedMoreTools.forEach((id) => {
        if (TOOL_BY_ID[id]) {
          schedule[id] = { active: true, freq: 'daily', time: 'anytime' };
        }
      });
      await this.routineSvc.saveSchedule(schedule);
      this.selectedMoreTools.clear();
      const [s, c] = await this.fetchData(true);
      this.paint(s, c);
    } catch (e) {
      console.error('HOME add tools failed:', e);
    } finally {
      this.isAddingTools = false;
    }
  }

  private lang(): string {
    return this.translation.getCurrentLanguage();
  }

  img(id: string): string {
    return toolImg(id);
  }

  openTool(id: string): void {
    // Option A (tools locked for the unpaid user): every tile routes to the
    // paywall instead of opening the tool (spec §2 / §4).
    if (this.toolsLocked) {
      this.openPaywall();
      return;
    }
    this.router.navigate(['/' + this.lang() + '/app/tool/' + id]);
  }

  startAssessment(): void {
    // Web keeps scan reachable (flow tree: web as-is). Native phone-only gate
    // can be added here later via PlatformService.
    this.router.navigate(['/' + this.lang() + '/app/new-test']);
  }

  viewPrevious(): void {
    if (!this.prevScanGuid) return;
    this.router.navigate([
      '/' + this.lang() + '/app/results/' + this.prevScanGuid,
    ]);
  }

  // "הסקירה הראשונית שלך" → the FIRST (free) scan's results. Free forever, so it
  // passes the server gate and renders the 4 domains (premium sections stay
  // locked); it must NOT go to the paywall.
  viewFirstResults(): void {
    if (!this.firstScanGuid) return;
    this.router.navigate([
      '/' + this.lang() + '/app/results/' + this.firstScanGuid,
    ]);
  }

}
